import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children?: React.ReactNode;
  content: string;
  className?: string;
  // Applied to the always-rendered sr-only copy so a trigger can reference it
  // with aria-describedby and keep a stable description whether or not it shows.
  id?: string;
  /** `right` sits beside the trigger, for triggers at the viewport's left edge. */
  placement?: 'auto' | 'right';
  /** Never shows the bubble (the sr-only copy stays), e.g. while the trigger's own panel is open. */
  disabled?: boolean;
}

type Position = { top: number; left: number; side: 'above' | 'below' | 'right' };

const TRANSFORMS: Record<Position['side'], string> = {
  above: 'translate(-50%, -100%)',
  below: 'translate(-50%, 0)',
  right: 'translate(0, -50%)',
};

const ARROW_CLASSES: Record<Position['side'], string> = {
  above: 'left-1/2 -translate-x-1/2 -bottom-1',
  below: 'left-1/2 -translate-x-1/2 -top-1',
  right: 'top-1/2 -translate-y-1/2 -left-1',
};

// The tooltip bubble is portalled to document.body with fixed positioning so it
// escapes overflow-clipped ancestors (e.g. a data table's overflow-x-auto) and
// is never stacked behind the nav. It shows on hover/focus of the trigger.
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className = '',
  id,
  placement = 'auto',
  disabled = false,
}) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  // Keyed on visibility, not hover or focus, so disabling a showing tooltip also drops its Escape listener.
  const visible = show && !disabled;
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spacing = 8;
    if (placement === 'right') {
      setPos({ top: rect.top + rect.height / 2, left: rect.right + spacing, side: 'right' });
      return;
    }
    // Prefer above; drop below when there is not enough room at the top.
    const above = rect.top >= 40 + spacing;
    setPos({
      top: above ? rect.top - spacing : rect.bottom + spacing,
      left: rect.left + rect.width / 2,
      side: above ? 'above' : 'below',
    });
  }, [visible, placement]);

  // A fixed-position bubble can't follow scroll/resize, so hide it to avoid drift.
  // Escape is swallowed only while the trigger owns focus, so a parent dialog still closes.
  useEffect(() => {
    if (!visible) return;
    const hide = () => setShow(false);
    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (triggerRef.current?.contains(document.activeElement)) event.stopPropagation();
      setShow(false);
    };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', hideOnEscape, true);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', hideOnEscape, true);
    };
  }, [visible]);

  return (
      <span
          ref={triggerRef}
          className="inline-flex items-center"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onFocus={() => setShow(true)}
          onBlur={() => setShow(false)}
      >
          {children}
          <span id={id} className="sr-only">
              {content}
          </span>
          {visible &&
              pos &&
              createPortal(
                  <div
                      role="tooltip"
                      style={{
                          position: 'fixed',
                          top: pos.top,
                          left: pos.left,
                          transform: TRANSFORMS[pos.side],
                          // Above the shared Modal overlay (z-99999, see ui/modal/Modal.tsx) so a tooltip
                          // portalled from inside a modal is not painted behind it; below the toast layer
                          // (999999, see AppLayout.tsx). Matches the shared Select menu.
                          zIndex: 100000,
                      }}
                      className={`pointer-events-none max-w-xs whitespace-normal rounded bg-mp-tooltip px-2 py-1 text-xs text-mp-tooltip-foreground shadow-lg ${className}`}
                  >
                      {content}
                      {/* Arrow pointing back at the trigger; flips with the placement. */}
                      <span className={`absolute h-2 w-2 rotate-45 bg-mp-tooltip ${ARROW_CLASSES[pos.side]}`} />
                  </div>,
                  document.body
              )}
      </span>
  );
};
